import { useEffect, useState } from "react";
import { fetchAdminRulebookStatus, syncAdminRulebook } from "../../../lib/ligamanagerApi";

const DEFAULT_DOC_URL = "https://docs.google.com/document/d/1LYBGDxeZfWfW22j48THJKAMXMre-uqSCISnQ0nphE9o/edit?tab=t.0";

function prettyDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (err instanceof Error) return err.message || "Fehler";
    return String(err);
}

export default function RulebookManagement() {
    const [status, setStatus] = useState(null);
    const [docUrl, setDocUrl] = useState(DEFAULT_DOC_URL);
    const [title, setTitle] = useState("GT3 Xperience Regelwerk");
    const [force, setForce] = useState(false);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [err, setErr] = useState("");
    const [message, setMessage] = useState("");

    async function loadStatus() {
        setLoading(true);
        setErr("");

        try {
            const data = await fetchAdminRulebookStatus("gt3x");
            const rb = data?.rulebook || null;
            setStatus(rb);
            if (rb?.google_doc_url) setDocUrl(rb.google_doc_url);
            if (rb?.title) setTitle(rb.title);
        } catch (e) {
            setErr(prettyError(e));
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadStatus();
    }, []);

    async function handleSync() {
        setSyncing(true);
        setErr("");
        setMessage("");

        try {
            const data = await syncAdminRulebook({
                slug: "gt3x",
                title,
                google_doc_url: docUrl,
                force,
            });

            const changed = Boolean(data?.sync?.changed);
            setMessage(changed ? "Regelwerk wurde synchronisiert." : "Regelwerk war bereits aktuell.");
            await loadStatus();
        } catch (e) {
            setErr(prettyError(e));
        } finally {
            setSyncing(false);
        }
    }

    const hasContent = Boolean(status?.has_content);
    const syncStatus = String(status?.sync_status || "never");

    return (
        <div className="rulebook-admin-page">
            <section className="rulebook-hero rulebook-admin-hero">
                <div>
                    <div className="rulebook-kicker">Admin</div>
                    <h1>Regelwerk Sync</h1>
                    <p>
                        Google Docs bleibt die Bearbeitungsquelle. RaceVision zieht daraus HTML, bereinigt es und
                        speichert es schnell abrufbar in der Datenbank.
                    </p>
                </div>

                <div className="rulebook-meta-grid">
                    <div className="rulebook-meta-card">
                        <span>Status</span>
                        <strong>{syncStatus === "ok" ? "Aktuell" : syncStatus === "error" ? "Fehler" : "Noch kein Sync"}</strong>
                    </div>
                    <div className="rulebook-meta-card">
                        <span>Letzter Sync</span>
                        <strong>{prettyDate(status?.last_synced_at)}</strong>
                    </div>
                    <div className="rulebook-meta-card">
                        <span>Inhalt</span>
                        <strong>{hasContent ? `${status?.content_length || 0} Zeichen` : "leer"}</strong>
                    </div>
                </div>
            </section>

            {err && (
                <section className="card rulebook-state-card rulebook-state-card--error">
                    <h2>Fehler</h2>
                    <p>{err}</p>
                </section>
            )}

            {message && (
                <section className="card rulebook-state-card rulebook-state-card--success">
                    <h2>Erledigt</h2>
                    <p>{message}</p>
                </section>
            )}

            <section className="rulebook-admin-grid">
                <article className="card rulebook-admin-card">
                    <h2>Quelle</h2>
                    <p className="muted">
                        Trage hier den Google-Docs-Link ein. Wichtig: Das Dokument muss für die Service-Account-Mail
                        freigegeben sein, sonst darf das Backend es nicht exportieren.
                    </p>

                    <label className="rulebook-form-label">
                        Titel
                        <input
                            className="input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="GT3 Xperience Regelwerk"
                        />
                    </label>

                    <label className="rulebook-form-label">
                        Google Docs Link oder Document ID
                        <input
                            className="input"
                            value={docUrl}
                            onChange={(e) => setDocUrl(e.target.value)}
                            placeholder={DEFAULT_DOC_URL}
                        />
                    </label>

                    <label className="rulebook-check-row">
                        <input
                            type="checkbox"
                            checked={force}
                            onChange={(e) => setForce(e.target.checked)}
                        />
                        <span>Sync erzwingen, auch wenn Google-Version unverändert ist</span>
                    </label>

                    <div className="rulebook-admin-actions">
                        <button
                            type="button"
                            className="button button--primary"
                            onClick={handleSync}
                            disabled={syncing || loading}
                        >
                            {syncing ? "Synchronisiere…" : "Jetzt synchronisieren"}
                        </button>

                        <button
                            type="button"
                            className="button button--secondary"
                            onClick={loadStatus}
                            disabled={syncing || loading}
                        >
                            Status neu laden
                        </button>
                    </div>
                </article>

                <article className="card rulebook-admin-card">
                    <h2>Status</h2>
                    {loading ? (
                        <p>Lade Status…</p>
                    ) : (
                        <div className="rulebook-status-list">
                            <div><span>Slug</span><strong>{status?.slug || "gt3x"}</strong></div>
                            <div><span>Google Version</span><strong>{status?.google_version || "—"}</strong></div>
                            <div><span>Google geändert</span><strong>{prettyDate(status?.google_modified_time)}</strong></div>
                            <div><span>Zuletzt synchronisiert</span><strong>{prettyDate(status?.last_synced_at)}</strong></div>
                            <div><span>Content Hash</span><strong>{status?.content_hash ? `${String(status.content_hash).slice(0, 12)}…` : "—"}</strong></div>
                            <div><span>Veröffentlicht</span><strong>{status?.is_active ? "Ja" : "Nein"}</strong></div>
                        </div>
                    )}

                    {status?.google_doc_url && (
                        <a className="button button--secondary rulebook-admin-link" href={status.google_doc_url} target="_blank" rel="noreferrer">
                            Google Doc öffnen
                        </a>
                    )}

                    {status?.sync_error && (
                        <div className="rulebook-sync-error">
                            <strong>Letzter Sync-Fehler</strong>
                            <p>{status.sync_error}</p>
                        </div>
                    )}
                </article>
            </section>
        </div>
    );
}
